import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { fluxoCaixaService } from '../services/fluxoCaixaService';
import { agruparPorCategoria } from '../utils/fluxoCaixaCalculations';
import {
  FluxoCaixaCategoriaLinha,
  FluxoCaixaEvolucaoMensal,
  FluxoCaixaPeriodo,
  FluxoCaixaTipo,
  getDefaultPeriodoResumo,
} from '../types/fluxoCaixa';

export function useFluxoCaixaRelatorio(tenantId?: number) {
  const [periodo, setPeriodo] = useState<FluxoCaixaPeriodo>(getDefaultPeriodoResumo);
  const [tipo, setTipo] = useState<FluxoCaixaTipo | null>(null);
  const [linhas, setLinhas] = useState<FluxoCaixaCategoriaLinha[]>([]);
  const [evolucao, setEvolucao] = useState<FluxoCaixaEvolucaoMensal[]>([]);
  const [loadingRelatorio, setLoadingRelatorio] = useState(true);
  const [loadingEvolucao, setLoadingEvolucao] = useState(true);

  const carregarRelatorio = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoadingRelatorio(true);
      const lancamentos = await fluxoCaixaService.listar(tenantId, {
        dataInicio: periodo.dataInicio,
        dataFim: periodo.dataFim,
        tipo,
        categorias: [],
        centrosCusto: [],
      });
      setLinhas(agruparPorCategoria(lancamentos));
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Erro ao carregar relatório por categoria');
      toast.error(e.message);
    } finally {
      setLoadingRelatorio(false);
    }
  }, [tenantId, periodo, tipo]);

  const carregarEvolucao = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoadingEvolucao(true);
      const dados = await fluxoCaixaService.obterEvolucaoMensal(tenantId, 3);
      setEvolucao(dados);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Erro ao carregar evolução mensal');
      toast.error(e.message);
    } finally {
      setLoadingEvolucao(false);
    }
  }, [tenantId]);

  useEffect(() => {
    carregarRelatorio();
  }, [carregarRelatorio]);

  useEffect(() => {
    carregarEvolucao();
  }, [carregarEvolucao]);

  const recarregar = useCallback(async () => {
    await Promise.all([carregarRelatorio(), carregarEvolucao()]);
  }, [carregarRelatorio, carregarEvolucao]);

  return {
    periodo,
    setPeriodo,
    tipo,
    setTipo,
    linhas,
    evolucao,
    loadingRelatorio,
    loadingEvolucao,
    recarregar,
  };
}
